// BUG-2443 (same till on a second device): which device holds a till on the server.
//
// The server keeps one cwr_tillreg_history row per registration. tillRegistration
// refuses a till that has an active row ("This Till is already linked to another
// device"), Close Till (LO) and unlinkTill make rows inactive, getTills returns the
// active rows. Before this change every row was written with empty device fields, so
// nobody could tell which device held a till.
//
// Every registration now carries this browser's device id in `sessionid` (prefixed
// POS_DEVICE_MARKER) plus readable details. The marker is what makes "this till was
// moved to another device" safe to act on: on 2026-10-09, 66 live tills that bill
// every day held an inactive registration while an older active row from another
// device sat on the till (registered once, weeks earlier). Those rows carry no
// marker, so they are left alone; only a row written by this code (a real "Use POS
// Here" or a registration on another device) stops this device.

import { parseTillAccess } from "../constants/accessControl";

export const POS_DEVICE_ID_KEY = "cw_pos_device_id";
export const POS_DEVICE_MARKER = "CWPOS-";

// cwr_tillreg_history column sizes (same on all six live DBs, 2026-10-09).
const MAX = { sessionid: 60, domain: 60, osVersion: 60, processor: 30 };

const randomHex = () => {
  try {
    const bytes = new Uint8Array(12);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("").toUpperCase();
  } catch {
    return (Date.now().toString(16) + Math.random().toString(16).slice(2)).slice(0, 24).toUpperCase();
  }
};

// One id per browser profile (CW Browser keeps localStorage across restarts). A
// cleared cache gets a new id, which is right: that browser no longer holds the
// till's local data either.
export function getPosDeviceId() {
  try {
    let id = localStorage.getItem(POS_DEVICE_ID_KEY);
    if (!id || !id.startsWith(POS_DEVICE_MARKER)) {
      id = POS_DEVICE_MARKER + randomHex();
      localStorage.setItem(POS_DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

export const isPosDeviceMarker = (value) => typeof value === "string" && value.startsWith(POS_DEVICE_MARKER);

// The mutation is built as a string, so only plain characters go in, cut to the column size.
export const cleanDeviceValue = (value, max) => {
  if (value === null || value === undefined) return null;
  const v = String(value)
    .replace(/[^A-Za-z0-9 ._\-/():,]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max)
    .trim();
  return v || null;
};

const browserOf = (ua, win) => {
  if (win?.electronAPI?.isElectron) return "CW Browser";
  if (win?.electronAPI?.isMobile) return "CW Mobile";
  const m = ua.match(/Edg\/(\d+)/) || ua.match(/OPR\/(\d+)/) || ua.match(/Firefox\/(\d+)/) || ua.match(/Chrome\/(\d+)/) || ua.match(/Version\/(\d+).*Safari/);
  if (!m) return "Browser";
  const name = m[0].startsWith("Edg") ? "Edge" : m[0].startsWith("OPR") ? "Opera" : m[0].startsWith("Firefox") ? "Firefox" : m[0].startsWith("Chrome") ? "Chrome" : "Safari";
  return `${name} ${m[1]}`;
};

const osOf = (ua) => {
  if (/Android/i.test(ua)) return "Android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "iOS";
  if (/Windows/i.test(ua)) return "Windows";
  if (/Mac OS X|Macintosh/i.test(ua)) return "Mac";
  if (/CrOS/i.test(ua)) return "ChromeOS";
  if (/Linux/i.test(ua)) return "Linux";
  return "";
};

// Fields for tillRegistration's tillHistory. processor holds "browser on OS" because
// getTills does not return osVersion and the popup needs something a cashier recognises.
export function readDeviceDetails(win = typeof window !== "undefined" ? window : undefined) {
  const nav = win?.navigator || {};
  const ua = nav.userAgent || "";
  const browser = browserOf(ua, win);
  const os = osOf(ua);
  const mem = Number(nav.deviceMemory);
  return {
    sessionid: cleanDeviceValue(getPosDeviceId(), MAX.sessionid),
    domain: cleanDeviceValue(win?.location?.host, MAX.domain),
    osVersion: cleanDeviceValue([os, nav.platform].filter(Boolean).join(" "), MAX.osVersion),
    processor: cleanDeviceValue(os ? `${browser} on ${os}` : browser, MAX.processor),
    ram: Number.isFinite(mem) && mem > 0 ? Math.ceil(mem) : null,
  };
}

const gqlString = (v) => (v === null || v === undefined ? "null" : `"${v}"`);

// The tillHistory body for the tillRegistration mutation (regTimestamp added by the caller).
export function deviceDetailsGql(details) {
  const d = details || {};
  return `domain: ${gqlString(d.domain)}
                  sessionid: ${gqlString(d.sessionid)}
                  osVersion: ${gqlString(d.osVersion)}
                  localIpAddress: null
                  macAddress: null
                  processor: ${gqlString(d.processor)}
                  ram: ${Number.isInteger(d.ram) ? d.ram : "null"}`;
}

export const isAlreadyLinkedMessage = (message) => /already linked/i.test(String(message || ""));

// upsertTill's answer when the till's day is still open on the server (TillDaoImpl
// upsertTill, dailyops_till open_till = Y): another device has not done Close Till yet.
export const isCloseTillPendingMessage = (message) => /close till is pending/i.test(String(message || ""));

// Restaurant tills (cwr_till.isrestaurantpos = Y, POS layout 2) may be registered on
// several devices by design (the backend only counts the same user's rows), so they
// keep today's behaviour. Production tills (posType PRD) too. So do templates with
// showTillOpening "N" that are not estimate tills: they open on the device with no server
// day open, so a second device could not be told to wait for the first one's Close Till.
// Estimate tills (enableEstimate "Y") can move: estimates are saved online only and the
// server gives their number, so there is no day, bill counter or unsent queue to hand over
// (live 2026-10-12: the 20 GPR showTillOpening "N" tills are all estimate tills, 0 bills in
// 30 days, shiftClose "N"; BUG-2443 itself was RDG - E3).
export const isEstimateOnlyTill = (posConfig) => posConfig?.showTillOpening !== "Y" && posConfig?.enableEstimate === "Y";

export function isTillMoveSupported({ tillAccessController, posType, posConfig }) {
  if (posType === "PRD") return false;
  if (posConfig?.showTillOpening !== "Y" && !isEstimateOnlyTill(posConfig)) return false;
  return String(tillAccessController?.layout ?? "1") !== "2";
}

// Orders on this device the server does not have yet: 0 = not sent, 2 = failed (retried),
// 3 = being sent (DI-16). Read by the isSynced index (CW Browser keeps it as text).
export async function countUnsentOrders(db) {
  const rows = await db.orders.where("isSynced").anyOf([0, 2, 3, "0", "2", "3"]).toArray();
  return (rows || []).length;
}

// Raise-only refresh of the three bill counters from the server when a till's day is
// opened. A device that took the till over (Use POS Here) read the counters when it
// signed in, which can be before the other device's last bills reached the server; the
// POS-mount reconcile (BUG-2468) re-reads only the regular counter. A counter is never
// lowered, so when nothing changed on the server this writes nothing.
//   readState() -> getTillSequenceState result or null (offline: nothing changes)
export async function raiseTillSequencesFromServer({ sessionStore, readState, logEvent = () => {} }) {
  const server = await readState();
  if (!server) return { reached: false, changed: [] };
  const s = sessionStore.getState();
  const changed = [];

  const regular = Math.max(0, (Number(server.nextAssignedNumber) || 0) - 1);
  const currentRegular = Number(s.documentSequence) || 0;
  if (regular > currentRegular) {
    s.setDocumentSequence(regular);
    changed.push({ counter: "regular", from: currentRegular, to: regular });
  }

  const raise = (current, fromServer, setter, counter) => {
    if (!current || !fromServer || current.prefix !== fromServer.prefix) return;
    const serverNo = Number(fromServer.nextNo) || 0;
    const currentNo = Number(current.nextNo) || 0;
    if (serverNo <= currentNo) return;
    setter({ ...current, nextNo: serverNo });
    changed.push({ counter, from: currentNo, to: serverNo });
  };
  raise(s.preorderSequence, server.preorderSequence, s.setPreorderSequence, "preorder");
  raise(s.cancelledOrderSequence, server.cancelledOrderSequence, s.setCancelledOrderSequence, "cancelled");

  logEvent({ server, changed });
  return { reached: true, changed };
}

// The till row registered on this browser: db.tillRegistrations row [0] is what Close
// Till's LO and every other consumer reads.
export function heldRegistration(rows) {
  const row = Array.isArray(rows) ? rows[0] : null;
  if (!row) return null;
  const access = Array.isArray(row.tillAccess) ? row.tillAccess[0] : row.tillAccess;
  const cwrTill = access?.cwrTill || {};
  return {
    rowId: row.id,
    heldId: row.tillHistory?.[0]?.cwrTillRegHistoryId || null,
    till: cwrTill.till || null,
    searchKey: cwrTill.searchKey || null,
  };
}

const newest = (rows) => [...rows].sort((a, b) => String(b?.regTimestamp || "").localeCompare(String(a?.regTimestamp || "")))[0];

// held          this browser's registration is active
// own_row       an active row carries this browser's device id but a different
//               registration id (re-registered, local save did not finish): adopt it
// free          no active row (normal after Close Till: LO makes the row inactive)
// moved         another device running this code holds the till
// unknown       only rows without a device id (older POS / stray registrations): no action
export function classifyTillHold({ activeRows, heldId, deviceId }) {
  const rows = (Array.isArray(activeRows) ? activeRows : []).filter(Boolean);
  if (heldId && rows.some((r) => r.cwrTillRegHistoryId === heldId)) return { state: "held" };
  const own = deviceId ? rows.filter((r) => r.sessionid === deviceId) : [];
  if (own.length > 0) return { state: "own_row", row: newest(own) };
  if (rows.length === 0) return { state: "free" };
  const marked = rows.filter((r) => isPosDeviceMarker(r.sessionid));
  if (marked.length > 0) return { state: "moved", row: newest(marked) };
  return { state: "unknown", row: newest(rows) };
}

// Same browser type and address as this one, but another device id: most often this
// computer in another browser profile (each profile has its own id), or this browser
// after its data was cleared (the old id went with it). A best guess: two computers
// with the same browser version look the same.
export const looksLikeThisBrowser = (row, here = readDeviceDetails()) =>
  isPosDeviceMarker(row?.sessionid) && row.sessionid !== here.sessionid && !!row.processor && row.processor === here.processor && row.domain === here.domain;

// Shown on the profile / device that is about to take the till.
export const SAME_COMPUTER_HINT =
  "It looks like the same computer, in another browser profile. If this browser's data was cleared instead, bills saved before the clear that had not reached the server cannot be sent from here.";
// Shown on the profile that was stopped (it still has its own data).
export const SAME_COMPUTER_STOPPED_HINT = "It looks like the same computer, in another browser profile.";

// Plain lines a cashier and support can read. `hint`: the same-computer line to use.
export function describeTillHolder(row, till, here, hint = SAME_COMPUTER_HINT) {
  const lines = [];
  if (row?.processor) lines.push(`Device: ${row.processor}`);
  if (row?.domain) lines.push(`Opened from: ${row.domain}`);
  if (row?.regTimestamp) lines.push(`Linked at: ${row.regTimestamp}`);
  if (till?.userName) lines.push(`Last signed in: ${till.userName}`);
  if (!isPosDeviceMarker(row?.sessionid)) lines.push("No device details (linked by an older POS version).");
  else if (looksLikeThisBrowser(row, here)) lines.push(hint);
  return lines;
}

const newSyncIdDefault = () => {
  const hex = randomHex() + randomHex();
  return hex.slice(0, 32);
};

const regOf = (res) => res?.data?.data?.tillRegistration;

// Till selection: tillRegistration, and when the backend answers "already linked to
// another device", ask (confirm) and on Use POS Here unlinkTill + register again.
//   register(syncId) -> tillRegistration response, unlink() -> unlinkTill response,
//   confirm() -> Promise<boolean>. Returns { resData, tillData, syncId }.
export async function registerTillWithUseHere({ register, unlink, confirm, moveSupported, newSyncId = newSyncIdDefault, onAsk, log = () => {} }) {
  let syncId = newSyncId();
  let resData = await register(syncId);
  let tillData = regOf(resData);
  if (tillData?.status === "200" || !isAlreadyLinkedMessage(tillData?.message) || !moveSupported) return { resData, tillData, syncId };
  onAsk?.();
  const useHere = await confirm();
  log("Use POS Here answer", { useHere });
  if (!useHere) return { resData, tillData, syncId };
  const unlinked = (await unlink())?.data?.data?.unlinkTill;
  if (unlinked?.status !== "200") {
    log("unlinkTill failed", { unlinked });
    return { resData, syncId, tillData: { ...tillData, message: unlinked?.message || "Could not move the till to this device. Please try again." } };
  }
  syncId = newSyncId();
  resData = await register(syncId);
  tillData = regOf(resData);
  log("registered after Use POS Here", { status: tillData?.status, message: tillData?.message });
  return { resData, tillData, syncId };
}

// Resume at login: Close Till (LO) makes this browser's registration inactive, so a
// till in daily use looks free to every other device, which would then register it
// with no prompt. Hold it again for this device when (and only when) it is free:
// a new registration row, replaced in place on db.tillRegistrations row [0]. The bill
// counter and syncId are not touched. Returns the action taken.
export async function reholdTillIfFree({ db, tillData, posType, posConfig, readTill, register, deviceId = getPosDeviceId(), newSyncId = newSyncIdDefault, log = () => {} }) {
  const cwrTill = tillData?.tillAccess?.cwrTill;
  if (!cwrTill?.cwrTillID) return "no_till";
  if (!isTillMoveSupported({ tillAccessController: parseTillAccess(tillData), posType, posConfig })) return "not_supported";
  const rows = await db.tillRegistrations.toArray();
  const held = heldRegistration(rows);
  if (!held?.heldId || (held.till && held.till !== cwrTill.till)) {
    log("re-hold skipped (row [0] is not this till)", { held, till: cwrTill.till });
    return "other_row";
  }
  const till = await readTill(cwrTill.cwrTillID);
  if (!till) return "not_listed";
  const hold = classifyTillHold({ activeRows: till.tillHistory, heldId: held.heldId, deviceId });
  log("till hold at login", { till: cwrTill.till, heldId: held.heldId, state: hold.state, row: hold.row });
  if (hold.state === "own_row") {
    await replaceHeldTillHistory(db, held.rowId, [{ ...rows[0].tillHistory[0], ...hold.row }]);
    return "own_row";
  }
  if (hold.state !== "free") return hold.state;
  const reg = regOf(await register(newSyncId(), till.searchKey || cwrTill.searchKey));
  const ok = reg?.status === "200" && reg.tillHistory?.length > 0;
  if (ok) await replaceHeldTillHistory(db, held.rowId, reg.tillHistory);
  log("till re-held at login", { till: cwrTill.till, status: reg?.status, message: reg?.message, newId: reg?.tillHistory?.[0]?.cwrTillRegHistoryId });
  return ok ? "reheld" : "rehold_refused";
}

// Replace the registration on db.tillRegistrations row [0] in place (never append:
// consumers read row [0]). The bill counter is not touched.
export async function replaceHeldTillHistory(db, rowId, tillHistory) {
  if (rowId === undefined || rowId === null || !Array.isArray(tillHistory) || tillHistory.length === 0) return false;
  await db.tillRegistrations.update(rowId, { tillHistory });
  return true;
}
